<template>
	<section class="alerts">
		<section class="b360-grid-row preference-center-section">
			<title-description
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4 b360-col-lg-4 form-title-description"
				title="Alerts"
				description="Choose the event(s) you’d like to set an alert for and we’ll automatically notify your clients."
			></title-description>

			<section
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-8"
				:key="userId"
			>
				<AlertBoost :show="canAccessBoost" data-testid="alert-feature" />
				<h2 class="alerts-subheading">Manage Alerts</h2>
				<section v-if="alerts !== null && alerts !== undefined">
					<Accordion
						:alertOn="reviewMe.isOptedIn"
						:name="reviewMe.name"
						accordionLabel="Review Me"
						:tooltip="PC_ALERT_TOOLTIPS['ReviewMe']"
					>
						<AlertLayout
							slot="accordionContent"
							:noGMB="noGMB"
							:disabled="
								(disableSave || !canModifyClientConnectAlertSettings) && noGMB
							"
							:alert="reviewMe"
							:alertIndex="getPreferenceIndex(reviewMe.name)"
						/>
					</Accordion>
					<Accordion
						:alertOn="creditWatch.isOptedIn"
						:name="creditWatch.name"
						accordionLabel="Credit Watch"
						:tooltip="PC_ALERT_TOOLTIPS['CreditWatch']"
					>
						<AlertLayout
							slot="accordionContent"
							:disabled="disableSave || !canModifyClientConnectAlertSettings"
							:alert="creditWatch"
							:alertIndex="getPreferenceIndex(creditWatch.name)"
						/>
					</Accordion>

					<Accordion
						:alertOn="equityWatch.isOptedIn"
						:name="equityWatch.name"
						accordionLabel="Unite"
						:tooltip="PC_ALERT_TOOLTIPS['EquityWatch']"
					>
						<AlertLayout
							slot="accordionContent"
							:disabled="disableSave || !canModifyClientConnectAlertSettings"
							:alert="equityWatch"
							:alertIndex="getPreferenceIndex(equityWatch.name)"
						/>
					</Accordion>

					<Accordion
						:alertOn="MIDropEligible.isOptedIn"
						:name="MIDropEligible.name"
						accordionLabel="MI Drop Eligible"
						:tooltip="PC_ALERT_TOOLTIPS['MIDropEligible']"
					>
						<AlertLayout
							slot="accordionContent"
							:disabled="disableSave || !canModifyClientConnectAlertSettings"
							:alert="MIDropEligible"
							:alertIndex="getPreferenceIndex(MIDropEligible.name)"
						/>
					</Accordion>
					<Accordion
						:alertOn="marketWatch.isOptedIn"
						:name="marketWatch.name"
						accordionLabel="UConnect"
						:tooltip="PC_ALERT_TOOLTIPS['MarketWatch']"
					>
						<AlertLayout
							slot="accordionContent"
							:disabled="disableSave || !canModifyClientConnectAlertSettings"
							:alert="marketWatch"
							:alertIndex="getPreferenceIndex(marketWatch.name)"
						/>
					</Accordion>

					<Accordion
						:alertOn="newListing.isOptedIn"
						:name="newListing.name"
						accordionLabel="New Listings"
						:tooltip="PC_ALERT_TOOLTIPS['NewListing']"
					>
						<AlertLayout
							slot="accordionContent"
							:disabled="disableSave || !canModifyClientConnectAlertSettings"
							:alert="newListing"
							:alertIndex="getPreferenceIndex(newListing.name)"
						/>
					</Accordion>

					<!-- INFO: hiding due to KEEP go live, may unhide at a later date - Story: B360-3943 -->
					<!-- <Accordion
						accordionLabel="Rate Watch"
						:name="rateWatch.name"
						:alertOn="rateWatch.isOptedIn"
						:tooltip="PC_ALERT_TOOLTIPS['RateWatch']"
					>
						<AlertLayout
							slot="accordionContent"
							:isRateWatch="true"
							:disabled="disableSave || !canModifyClientConnectAlertSettings"
							:alert="rateWatch"
							:alertIndex="getPreferenceIndex(rateWatch.name)"
						/>
					</Accordion> -->
					<Accordion
						:alertOn="birthday.isOptedIn"
						:name="birthday.name"
						accordionLabel="Birthdays"
						:tooltip="PC_ALERT_TOOLTIPS['Birthday']"
					>
						<AlertLayout
							slot="accordionContent"
							:disabled="disableSave || !canModifyClientConnectAlertSettings"
							:alert="birthday"
							:alertIndex="getPreferenceIndex(birthday.name)"
						/>
					</Accordion>

					<Accordion
						:alertOn="loanAnniversary.isOptedIn"
						:name="loanAnniversary.name"
						accordionLabel="Loan Anniversary"
						:tooltip="`Alerts you when a borrower is celebrating a loan anniversary.`"
					>
						<AlertLayout
							slot="accordionContent"
							:disabled="disableSave || !canModifyClientConnectAlertSettings"
							:alert="loanAnniversary"
							:alertIndex="getPreferenceIndex(loanAnniversary.name)"
						/>
					</Accordion>
				</section>
				<SaveButton
					:waiting="savingState"
					:saved="savedState"
					:disabled="disableSave"
					@handle-click="saveUserAlerts"
				></SaveButton>
			</section>
		</section>
	</section>
</template>

<script>
import TitleDescription from '@/components/global/TitleDescription.vue';
import Accordion from '@/components/global/Accordion.vue';
import AlertBoost from '@/components/preference-center/AlertBoost.vue';
import SaveButton from '@/components/preference-center/SaveButton.vue';
import { mapState, mapActions, mapGetters } from 'vuex';
import profileApiClient from '@/services/profile/profile-service';
import { cloneDeep, isEqual } from 'lodash';
import { PC_ALERT_TOOLTIPS } from '@/components/preference-center/constants/preference-center-alerts-constants.js';
import AlertLayout from '@/components/preference-center/AlertsLayout.vue';
import { ERROR_DETAILS } from '@/constants/error-details';

export default {
	name: 'PreferenceCenterAlerts',
	components: {
		TitleDescription,
		Accordion,
		AlertBoost,
		AlertLayout,
		SaveButton,
	},
	data() {
		return {
			accordionData: {},
			alerts: [],
			savingState: false,
			savedState: false,
			canModifyClientConnectAlertSettings: true,
			noGMB: true,
			googleMyBusinessURL: '',
			originalAlerts: [],
			PC_ALERT_TOOLTIPS,
		};
	},
	props: {
		birthdayIndex: Number,
	},
	async created() {
		this.setReviewMeAccess();
		if (this.userAlerts === null || this.userAlerts === undefined) {
			return;
		}
		await this.setInitialAlertSettingsState();
		this.canSetGlobalAlertPreferences();
	},
	computed: {
		...mapGetters([
			'isPermissionActive',
			'hasAccessToClientConnect',
			'getPreferenceIndex',
			'canAccessBoost',
			'hasGoogleMyBusiness',
		]),
		...mapState({
			disableSave: (state) =>
				state.profileSwitcher.profileSelector.isInternalUser,
			userId: (state) => state.profile.userId,
			userRole: (state) => state.profile.roles,
			contactId: (state) => state.profile.contactId,
			socialMediaDetails: (state) => state.companyProfile.socialMediaDetails,
			userAlerts: (state) => state.userAlerts,
			preferences: (state) => state.userAlerts.preferences,
			isEdited: (state) => state.userAlerts.isEdited,
		}),
		rateWatch() {
			let index = this.alerts.findIndex((x) => x.name === 'RateWatch');
			return this.alerts[index];
		},
		creditWatch() {
			let index = this.alerts.findIndex((x) => x.name === 'CreditWatch');
			return this.alerts[index];
		},
		equityWatch() {
			let index = this.alerts.findIndex((x) => x.name === 'EquityWatch');
			return this.alerts[index];
		},
		MIDropEligible() {
			let index = this.alerts.findIndex((x) => x.name === 'MIDropEligible');
			return this.alerts[index];
		},
		marketWatch() {
			let index = this.alerts.findIndex((x) => x.name === 'MarketWatch');
			return this.alerts[index];
		},
		newListing() {
			let index = this.alerts.findIndex((x) => x.name === 'NewListing');
			return this.alerts[index];
		},
		birthday() {
			let index = this.alerts.findIndex((x) => x.name === 'Birthday');
			return this.alerts[index];
		},
		loanAnniversary() {
			let index = this.alerts.findIndex((x) => x.name === 'LoanAnniversary');
			return this.alerts[index];
		},
		reviewMe() {
			let index = this.alerts.findIndex((x) => x.name === 'ReviewMe');
			return this.alerts[index];
		},
	},
	methods: {
		...mapActions(['saveAlerts', 'updateAlertValue', 'showApiErrorModal']),
		setReviewMeAccess() {
			//checks if company has a GMB URL and will allow access to the global settings if it exists
			this.noGMB = !this.hasGoogleMyBusiness;
		},
		async setInitialAlertSettingsState() {
			this.alerts = this.preferences;

			this.accordionData = {
				creditWatch: this.creditWatch,
				equityWatch: this.equityWatch,
				MIDropEligible: this.MIDropEligible,
				marketWatch: this.marketWatch,
				newListing: this.newListing,
				rateWatch: this.rateWatch,
				birthday: this.birthday,
				loanAnniversary: this.loanAnniversary,
				reviewMe: this.reviewMe,
			};
			this.originalAlerts = cloneDeep(this.alerts);
		},
		async saveUserAlerts() {
			// TODO: modularize this
			this.savingState = true;
			if (document.getElementById('preference-center-form').reportValidity()) {
				try {
					await this.saveAlerts({
						contactId: this.contactId,
						preferences: this.preferences,
					});
					this.savingState = false;
					this.savedState = true;
					this.originalAlerts = cloneDeep(this.alerts);
				} catch (error) {
					this.showApiErrorModal(ERROR_DETAILS.PREFERENCE_CENTER_ALERT_ERROR);
					this.savingState = false;
				}
			} else {
				this.savingState = false;
				const formElements = document.querySelectorAll('input, select');
				formElements.forEach((el) => {
					el.addEventListener('invalid', function () {
						this.scrollIntoView(false);
					});
				});
			}

			setTimeout(() => {
				this.savingState = false;
				this.savedState = false;
			}, 1000);
		},
		async canSetGlobalAlertPreferences() {
			if (
				this.userRole.includes('MarketingManager') &&
				(this.isPermissionActive(
					'MarketingManager',
					'ClientConnect',
					'SetGlobalAlertPreferences',
				) === false ||
					this.hasAccessToClientConnect === false)
			) {
				this.canModifyClientConnectAlertSettings = false;
				this.updateAlertValue({
					preferenceIndex: this.getPreferenceIndex('ReviewMe'),
					key: 'isAutoScheduled',
					value: false,
				});
				this.updateAlertValue({
					preferenceIndex: this.getPreferenceIndex('MarketWatch'),
					key: 'isAutoScheduled',
					value: false,
				});
				this.updateAlertValue({
					preferenceIndex: this.getPreferenceIndex('EquityWatch'),
					key: 'isAutoScheduled',
					value: false,
				});
				this.updateAlertValue({
					preferenceIndex: this.getPreferenceIndex('CreditWatch'),
					key: 'isAutoScheduled',
					value: false,
				});
				this.updateAlertValue({
					preferenceIndex: this.getPreferenceIndex('MIDropEligible'),
					key: 'isAutoScheduled',
					value: false,
				});
				this.updateAlertValue({
					preferenceIndex: this.getPreferenceIndex('RateWatch'),
					key: 'isAutoScheduled',
					value: false,
				});
				this.updateAlertValue({
					preferenceIndex: this.getPreferenceIndex('NewListing'),
					key: 'isAutoScheduled',
					value: false,
				});
				this.updateAlertValue({
					preferenceIndex: this.getPreferenceIndex('LoanAnniversary'),
					key: 'isAutoScheduled',
					value: false,
				});
				this.updateAlertValue({
					preferenceIndex: this.getPreferenceIndex('Birthday'),
					key: 'isAutoScheduled',
					value: false,
				});
				try {
					this.saveAlerts({
						contactId: this.contactId,
						preferences: this.preferences,
					});
				} catch (error) {
					console.error(error);
				}
			}
		},
		alertsHaveBeenEdited() {
			const _ = require('lodash');
			return !this.alerts.every((alert, index) =>
				_.isEqual(alert, this.originalAlerts[index]),
			);
		},
	},
	beforeRouteLeave(to, from, next) {
		if (this.alertsHaveBeenEdited()) {
			const answer = window.confirm(
				'There are unsaved changes in the form.  Are you sure you want to continue?',
			);
			// if the user has edited data and elects to navigate away without saving we rehydrate the store
			// with what was saved in the database
			if (answer === true) {
				profileApiClient.getProfile();
				this.$store.commit('ALERTS_ISEDITED');
				this.alerts = cloneDeep(this.originalAlerts);
			}
			next(answer);
		} else {
			next();
		}
	},
};
</script>

<style lang="scss" scoped>
.alerts-subheading {
	color: black;
	font-weight: 400;
	@include rem('font-size', 18px);
	@include rem('margin-bottom', 10px);
}
</style>
